import React, { useCallback, useEffect, useRef } from 'react';
import { observer } from 'mobx-react';
import { Intent } from '@blueprintjs/core';
import { Button, Dialog, Box } from 'core/components';
import DialogBody from 'core/components/DialogBody';
import DialogFooter from 'core/components/DialogFooter';
import { useFormContext } from 'core/form/FormContext';
import { Measurement } from '@kentik/ui-shared/query/query.types';
import { MeasurementHash } from 'app/stores/dictionary/useDictionaryMeasurements';
import UdeFilterOptions from './UdeFilterOptions';
import { FilterGroupsField, FilterGroupState, ConnectorField } from './filter.types';

interface UdeFilterModalProps {
  isOpen: boolean;
  onClose: () => void;
  title?: string;
  fieldName?: string;
  filterGroupsField?: FilterGroupsField;
  connectorField?: ConnectorField;
  measurement: string | null;
  measurements: Measurement[];
  measurementHash: MeasurementHash;
}

/**
 * Checks if a filter group has any valid filters
 */
function groupHasFilters(group: FilterGroupState): boolean {
  return group.filters.fieldStates.length > 0;
}

const UdeFilterModal = observer(
  ({
    isOpen,
    onClose,
    title = 'Filters',
    fieldName = 'filters',
    filterGroupsField,
    connectorField,
    measurement,
    measurements,
    measurementHash
  }: UdeFilterModalProps) => {
    const form = useFormContext();
    const lastValueRef = useRef<string | null>(null);

    const getField = useCallback(
      (): FilterGroupsField | null =>
        filterGroupsField || (form?.getField(`${fieldName}.filterGroups`) as FilterGroupsField) || null,
      [form, filterGroupsField, fieldName]
    );

    const saveLastValue = useCallback(() => {
      const field = getField();
      if (field) {
        lastValueRef.current = JSON.stringify(field.getValue());
      }
    }, [getField]);

    // Save value when modal opens
    useEffect(() => {
      if (isOpen) {
        saveLastValue();
      }
    }, [isOpen, saveLastValue]);

    const removeEmptyGroups = useCallback(
      (groups?: FilterGroupsField) => {
        const field = groups || getField();
        if (!field) {
          return;
        }

        // Iterate backwards to avoid index shifting when removing elements
        for (let i = field.fieldStates.length - 1; i >= 0; i--) {
          const group = field.fieldStates[i];
          if (!groupHasFilters(group)) {
            field.remove(i);
          }
        }
      },
      [getField]
    );

    const handleCancel = useCallback(
      (e?: React.MouseEvent | React.KeyboardEvent) => {
        // Standard event stoppage
        if (e) {
          e.preventDefault();
          e.stopPropagation();
        }

        // Handle revert cases (ESC key or Cancel button click)
        const isEscapeKey = e && 'key' in e && (e as React.KeyboardEvent).key === 'Escape';
        const isButtonClick =
          e && 'currentTarget' in e && (e.currentTarget as HTMLElement)?.className?.includes('bp4-button');

        if (e && (isEscapeKey || isButtonClick)) {
          onClose();
          if (lastValueRef.current) {
            const field = getField();
            if (field) {
              field.setValue(JSON.parse(lastValueRef.current));
            }
          }
        } else {
          onClose();
        }
      },
      [onClose, getField]
    );

    const handleSave = useCallback(() => {
      removeEmptyGroups();
      onClose();
    }, [removeEmptyGroups, onClose]);

    return (
      <Dialog
        isOpen={isOpen}
        onClose={handleCancel}
        width={700}
        position="absolute"
        top={50}
        maxHeight="calc(100vh - 150px)"
        title={title}
        tracker="ude-filters-dialog"
      >
        <DialogBody className="ude-filter-modal-body">
          <Box flex={1}>
            <UdeFilterOptions
              fieldName={fieldName}
              filterGroupsField={filterGroupsField}
              connectorField={connectorField}
              measurement={measurement}
              measurements={measurements}
              measurementHash={measurementHash}
            />
          </Box>
        </DialogBody>
        <DialogFooter>
          <Button text="Cancel" onClick={handleCancel} mr={1} width={110} />
          <Button intent={Intent.PRIMARY} text="Save" onClick={handleSave} width={110} ml={1} />
        </DialogFooter>
      </Dialog>
    );
  }
);

export default UdeFilterModal;
